/**
 * Encrypted group membership events (joins / leaves / kicks / rotations).
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/GroupMembershipEvent.swift`.
 *
 * Every member transition travels as a signed plaintext inside the standard
 * pairwise `kaether:e2ee:v1:` envelope. The plaintext itself carries:
 *
 *   1. **Outer Ed25519 authentication** — provided by the surrounding
 *      envelope layer; the actor's identity key signs the entire envelope so
 *      a third party cannot emit an event under another user's `actorUserId`.
 *
 *   2. **Inner HMAC-SHA256 under the group master key** — the sig field
 *      inside this plaintext proves the actor held the group master key when
 *      they emitted the event. An ex-member who lost master-key access cannot
 *      mint a new "add me back" envelope; their forged sig fails at decode.
 *
 * Wire format of the plaintext (NOT the outer `kaether:e2ee:v1:` envelope):
 *
 *   kaether:gmember:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   {
 *     "payload": {
 *       "schema": 1,
 *       "groupId": "<publicId-as-string>",
 *       "action": "added" | "joinedViaInvite" | "left" | "removed" | "mastered",
 *       "subjectUserId": "bob",
 *       "actorUserId": "alice",
 *       "ts": 1700000000,
 *       "reason": "spam" | null,
 *       "newGroupPublicIdAfterRotation": "<publicId-as-string>" | null
 *     },
 *     "sig": "<base64url(HMAC-SHA256 over canonical bytes under group master key)>"
 *   }
 *
 * Canonical bytes for HMAC signing (matching iOS exactly):
 *   schema ‖ groupId ‖ action ‖ subjectUserId ‖ actorUserId ‖ ts ‖ reason ‖ newGroupPublicIdAfterRotation
 *   UTF-8 encoded with separator 0x1f (ASCII unit separator). ts as 8-byte LE.
 */

import {
  GroupMasterKey,
  GroupIdentity,
  base64UrlEncode,
  base64UrlDecode,
} from "../group-identity/GroupIdentity";

export { GroupMasterKey } from "../group-identity/GroupIdentity";

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

export type GroupMembershipAction =
  | "added"
  | "joinedViaInvite"
  | "left"
  | "removed"
  | "mastered";

// ---------------------------------------------------------------------------
// Payload value type
// ---------------------------------------------------------------------------

export interface GroupMembershipPayload {
  readonly schema: 1;
  readonly groupId: string;
  readonly action: GroupMembershipAction;
  readonly subjectUserId: string;
  readonly actorUserId: string;
  readonly ts: number; // Unix seconds (Int64 on iOS, number on web — safe up to 2^53)
  readonly reason: string | null;
  readonly newGroupPublicIdAfterRotation: string | null;
}

/** Convenience constructor that pins schema = 1. */
export function makeGroupMembershipPayload(
  groupId: string,
  action: GroupMembershipAction,
  subjectUserId: string,
  actorUserId: string,
  ts: number,
  reason: string | null = null,
  newGroupPublicIdAfterRotation: string | null = null,
): GroupMembershipPayload {
  return {
    schema: 1,
    groupId,
    action,
    subjectUserId,
    actorUserId,
    ts,
    reason,
    newGroupPublicIdAfterRotation,
  };
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type MembershipCodecErrorKind =
  | { tag: "notAMembershipEvent" }
  | { tag: "malformed"; detail: string }
  | { tag: "unsupportedSchema"; version: number }
  | { tag: "invalidGroupSignature" }
  | { tag: "reasonTooLong"; count: number };

export class MembershipCodecError extends Error {
  readonly kind: MembershipCodecErrorKind;

  constructor(kind: MembershipCodecErrorKind) {
    super(kindToMessage(kind));
    this.name = "MembershipCodecError";
    this.kind = kind;
  }
}

function kindToMessage(k: MembershipCodecErrorKind): string {
  switch (k.tag) {
    case "notAMembershipEvent":
      return "not a membership event";
    case "malformed":
      return `malformed: ${k.detail}`;
    case "unsupportedSchema":
      return `unsupported schema: ${k.version}`;
    case "invalidGroupSignature":
      return "invalid group signature";
    case "reasonTooLong":
      return `reason too long: ${k.count} code points (max 200)`;
  }
}

// ---------------------------------------------------------------------------
// Wire-format JSON shape (with HMAC sig)
// ---------------------------------------------------------------------------

interface SignedEnvelope {
  payload: {
    schema: number;
    groupId: string;
    action: string;
    subjectUserId: string;
    actorUserId: string;
    ts: number;
    reason: string | null;
    newGroupPublicIdAfterRotation: string | null;
  };
  sig: string; // base64url-encoded HMAC-SHA256
}

// ---------------------------------------------------------------------------
// MembershipEventCodec
// ---------------------------------------------------------------------------

const VALID_ACTIONS = new Set<string>([
  "added",
  "joinedViaInvite",
  "left",
  "removed",
  "mastered",
]);

/**
 * Maximum number of unicode scalars (code points) allowed in the `reason`
 * field. Counted via `[...str].length` so emoji are correctly bounded.
 */
const MAX_REASON_LENGTH = 200;

/** Count unicode scalar values (code points), not UTF-16 code units. */
function countCodePoints(s: string): number {
  return [...s].length;
}

export const MembershipEventCodec = {
  PREFIX: "kaether:gmember:v1:" as const,

  /** Returns true iff `s` is a membership-event plaintext (prefix check). */
  isMembershipEvent(s: string): boolean {
    return s.startsWith("kaether:gmember:v1:");
  },

  /**
   * Encode a payload with an inner HMAC under `masterKey`. Throws
   * `MembershipCodecError` with `tag: "reasonTooLong"` if `reason` exceeds
   * 200 code points.
   *
   * The result is the plaintext that should be encrypted by the outer
   * `kaether:e2ee:v1:` envelope layer.
   */
  async encode(
    payload: GroupMembershipPayload,
    masterKey: GroupMasterKey,
  ): Promise<string> {
    // Enforce reason cap on encode.
    if (payload.reason !== null) {
      const cp = countCodePoints(payload.reason);
      if (cp > MAX_REASON_LENGTH) {
        throw new MembershipCodecError({ tag: "reasonTooLong", count: cp });
      }
    }

    const canonical = canonicalBytes(payload);
    const sigBytes = await GroupIdentity.signWithGroupKey(canonical, masterKey);
    const sig = base64UrlEncode(sigBytes);

    const envelope: SignedEnvelope = {
      payload: {
        schema: payload.schema,
        groupId: payload.groupId,
        action: payload.action,
        subjectUserId: payload.subjectUserId,
        actorUserId: payload.actorUserId,
        ts: payload.ts,
        reason: payload.reason,
        newGroupPublicIdAfterRotation: payload.newGroupPublicIdAfterRotation,
      },
      sig,
    };

    const json = JSON.stringify(envelope);
    const jsonBytes = new TextEncoder().encode(json);
    return "kaether:gmember:v1:" + base64UrlEncode(jsonBytes);
  },

  /**
   * Decode and verify the inner HMAC with the supplied master key. Throws
   * `MembershipCodecError` on any validation failure. The error kind
   * `invalidGroupSignature` is returned for both wrong-master-key and
   * tampered-bytes to avoid information leakage.
   */
  async decode(
    s: string,
    masterKey: GroupMasterKey,
  ): Promise<GroupMembershipPayload> {
    if (!s.startsWith("kaether:gmember:v1:")) {
      throw new MembershipCodecError({ tag: "notAMembershipEvent" });
    }

    const b64 = s.slice("kaether:gmember:v1:".length);
    const jsonBytes = base64UrlDecode(b64);
    if (!jsonBytes) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: "invalid base64url",
      });
    }

    let envelope: SignedEnvelope;
    try {
      const json = new TextDecoder().decode(jsonBytes);
      envelope = JSON.parse(json) as SignedEnvelope;
    } catch (e) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: `json decode failed: ${e}`,
      });
    }

    if (!envelope || typeof envelope !== "object" || !envelope.payload) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: "missing payload field",
      });
    }

    const p = envelope.payload;

    // Schema check.
    if (typeof p.schema !== "number" || p.schema !== 1) {
      throw new MembershipCodecError({
        tag: "unsupportedSchema",
        version: typeof p.schema === "number" ? p.schema : -1,
      });
    }

    // Required string fields.
    if (!p.groupId || typeof p.groupId !== "string" || p.groupId === "") {
      throw new MembershipCodecError({ tag: "malformed", detail: "empty groupId" });
    }
    if (
      !p.subjectUserId ||
      typeof p.subjectUserId !== "string" ||
      p.subjectUserId === ""
    ) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: "empty subjectUserId",
      });
    }
    if (
      !p.actorUserId ||
      typeof p.actorUserId !== "string" ||
      p.actorUserId === ""
    ) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: "empty actorUserId",
      });
    }

    // Action validation.
    if (!VALID_ACTIONS.has(p.action)) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: `unknown action: ${p.action}`,
      });
    }

    // Reason cap on decode (don't trust the wire).
    if (p.reason !== null && p.reason !== undefined) {
      const cp = countCodePoints(String(p.reason));
      if (cp > MAX_REASON_LENGTH) {
        throw new MembershipCodecError({ tag: "reasonTooLong", count: cp });
      }
    }

    // Reconstruct payload with validated/typed fields.
    const payload: GroupMembershipPayload = {
      schema: 1,
      groupId: p.groupId,
      action: p.action as GroupMembershipAction,
      subjectUserId: p.subjectUserId,
      actorUserId: p.actorUserId,
      ts: p.ts,
      reason: p.reason ?? null,
      newGroupPublicIdAfterRotation: p.newGroupPublicIdAfterRotation ?? null,
    };

    // Verify inner HMAC.
    const sigBytes = base64UrlDecode(envelope.sig);
    if (!sigBytes) {
      throw new MembershipCodecError({
        tag: "malformed",
        detail: "invalid signature encoding",
      });
    }

    const canonical = canonicalBytes(payload);
    const ok = await GroupIdentity.verifyWithGroupKey(
      canonical,
      sigBytes,
      masterKey,
    );
    if (!ok) {
      throw new MembershipCodecError({ tag: "invalidGroupSignature" });
    }

    return payload;
  },

  /**
   * Convenience: returns null for non-membership plaintexts. Throws only
   * for plaintexts that *did* claim to be membership events.
   */
  async tryDecode(
    s: string,
    masterKey: GroupMasterKey,
  ): Promise<GroupMembershipPayload | null> {
    if (!MembershipEventCodec.isMembershipEvent(s)) return null;
    return MembershipEventCodec.decode(s, masterKey);
  },
};

// ---------------------------------------------------------------------------
// Canonical bytes for HMAC signing
// ---------------------------------------------------------------------------

/**
 * Canonical bytes for the inner HMAC signature. Mirrors iOS exactly:
 *
 *   schema ‖ sep ‖ groupId ‖ sep ‖ action ‖ sep ‖ subjectUserId ‖ sep
 *   ‖ actorUserId ‖ sep ‖ ts-as-8-byte-LE ‖ sep ‖ (reason ?? "")
 *   ‖ sep ‖ (newGroupPublicIdAfterRotation ?? "")
 *
 * Separator = 0x1f (ASCII unit separator). Int64 as 8-byte little-endian.
 */
export function canonicalBytes(p: GroupMembershipPayload): Uint8Array {
  const sep = 0x1f;
  const enc = new TextEncoder();

  const schemaLE = int64LE(BigInt(p.schema));
  const tsLE = int64LE(BigInt(p.ts));

  const groupIdBytes = enc.encode(p.groupId);
  const actionBytes = enc.encode(p.action);
  const subjectBytes = enc.encode(p.subjectUserId);
  const actorBytes = enc.encode(p.actorUserId);
  const reasonBytes = enc.encode(p.reason ?? "");
  const rotBytes = enc.encode(p.newGroupPublicIdAfterRotation ?? "");

  // schema[8] + sep + groupId + sep + action + sep + subject + sep + actor
  //   + sep + ts[8] + sep + reason + sep + rot
  const totalLen =
    8 + 1 +
    groupIdBytes.length + 1 +
    actionBytes.length + 1 +
    subjectBytes.length + 1 +
    actorBytes.length + 1 +
    8 + 1 +
    reasonBytes.length + 1 +
    rotBytes.length;

  const out = new Uint8Array(totalLen);
  let pos = 0;

  const write = (bytes: Uint8Array) => {
    out.set(bytes, pos);
    pos += bytes.length;
  };
  const writeSep = () => {
    out[pos++] = sep;
  };

  write(schemaLE); writeSep();
  write(groupIdBytes); writeSep();
  write(actionBytes); writeSep();
  write(subjectBytes); writeSep();
  write(actorBytes); writeSep();
  write(tsLE); writeSep();
  write(reasonBytes); writeSep();
  write(rotBytes);

  return out;
}

/** Encode a BigInt as 8-byte little-endian Uint8Array. */
function int64LE(v: bigint): Uint8Array {
  const buf = new Uint8Array(8);
  let remaining = v;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return buf;
}
